<template>
  <main v-if="isApiCalled" class="evalFigma w-full summaryPg">
    <EvalShell title="Summary" :step="3" :job-id="$route.params.id" :unit-id="$route.query.unit_id">
      <!-- Figma D1: edit-note icon next to the unit chip → back to Equipment Diagnosis -->
      <template #title-extra>
        <a :href="'/equipment/' + jobId + `?unit_id=` + $route.query.unit_id + '&is_from_mobile=' + $route.query.is_from_mobile"
          class="w-[30px] h-[30px] rounded-full flex items-center justify-center bg-[#FFF1EB]"
          title="Edit equipment diagnosis" aria-label="Edit equipment diagnosis">
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#FE5000"
              d="M3 10h11v2H3zm0-4h11v2H3zm0 8h7v2H3zm17.59-2.07.71-.71a1 1 0 0 0 0-1.41l-.71-.71a1 1 0 0 0-1.41 0l-.71.71zm-.71.71-5.3 5.3H12v-2.12l5.3-5.3z" />
          </svg>
        </a>
      </template>

      <!-- Figma D5 (Service Required): banner at the top of the card after Next -->
      <div v-if="missingServiceCount > 0"
        class="flex items-center gap-[12px] mb-[20px] px-[18px] py-[13px] rounded-[5px] border-1 border-[#F5C2C2] bg-[#FDECEC] font-roboto">
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" class="flex-shrink-0">
          <path fill="#EE5851" d="M12 2 1 21h22z" />
          <path d="M12 9v5M12 17h.01" stroke="#FFFFFF" stroke-width="2" stroke-linecap="round" />
        </svg>
        <p class="m-0 text-[14px] leading-[20px] font-medium text-[#C43333]">
          Select a service for {{ missingServiceCount }} {{ missingServiceCount == 1 ? 'component' : 'components' }}
          before
          continuing to Finalize.
        </p>
      </div>

      <div v-if="options.length > 0" class="electricDuckSec flex flex-col gap-[20px]">
        <!-- flat price model: parts only, no service select -->
        <template v-if="bookingResponse.is_flat_price_model">
          <EvalComponentCard v-for="(item, index) in options" :id="index" :key="index" :title="item.option"
            :value="item.value">
            <EvalServiceRow :item="item" :show-select="false" :show-warranty="false"
              :parts-disabled="isDisableParts(item.selected_service)" @select-part="openPartsModal(item, index)" />
            <nest_selected_part :item="item" />
          </EvalComponentCard>
        </template>

        <template v-else>
          <EvalComponentCard v-for="(item, index) in options" :id="index" :key="index" :title="item.option"
            :value="item.value">
            <EvalServiceRow :item="item" :actions="getBookingActions(item.value) || []"
              :parts-disabled="isDisableParts(item.selected_service)" @service-change="onChange(item)"
              @select-part="openPartsModal(item, index)" />
            <nest_selected_part :item="item" />
          </EvalComponentCard>
        </template>
      </div>

      <div v-else class="py-[30px] px-[10px] text-center font-roboto">
        <p class="m-0 text-[14px] font-medium text-[#8A9099]">
          No items have been selected as required or recommended repairs.
        </p>
        <p class="m-0 mt-[4px] text-[12px] text-[#A8AEB8]">
          Either hit "Next" to complete this job or use the edit icon to change the diagnosis.
        </p>
      </div>

      <template #after>
        <!-- Figma D1: "Add Service. Items" card -->
        <div
          class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] px-[15px] laptop:px-[20px] py-[20px] bg-white font-roboto">
          <div class="flex flex-wrap items-center justify-between gap-2 pb-[15px]">
            <div class="mobile:basis-full laptop:basis-auto flex items-center gap-1">
              <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
              <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
              <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Add Service Items</p>
            </div>
            <!-- below laptop (Figma mobile): own row on the right, filled -->
            <button type="button"
              class="mobile:ml-auto laptop:ml-0 flex items-center gap-[10px] h-[36px] pl-[5px] pr-[15px] rounded-full border-1 text-[14px] mobile:bg-[#7695FF] mobile:border-[#7695FF] mobile:text-white laptop:bg-[#fff] laptop:border-[#E9ECF1] laptop:text-[#333333]"
              @click="addClicked()">
              <span
                class="w-[26px] h-[26px] rounded-full flex items-center justify-center bg-[#7695FF] mobile:border-[1.5px] mobile:border-white laptop:border-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="9" cy="8" r="3.5" stroke="#FFFFFF" stroke-width="1.8" />
                  <path d="M3 19c0-3 2.7-5 6-5s6 2 6 5M18 8v6M15 11h6" stroke="#FFFFFF" stroke-width="1.8"
                    stroke-linecap="round" />
                </svg>
              </span>
              Add Service
            </button>
          </div>
          <nest_added_services v-if="addedServices.length > 0" :services="addedServices" @edit="editService"
            @remove="removeService" @select-part="(item, index) => openPartsModal(item, options.length + index)" />
          <div v-else class="py-[20px] text-center">
            <p class="m-0 text-[14px] leading-[20px] font-medium text-[#8A9099]">No services added</p>
            <p class="m-0 text-[12px] leading-[19px] text-[#A8AEB8]">Add a service for this unit.</p>
          </div>
        </div>

        <!-- Figma D1: "Add Misc. Items" card -->
        <div
          class="additionalSec shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] px-[15px] laptop:px-[20px] py-[20px] bg-white">
          <nest_additional_table :additional-items="additionalItems" />
        </div>

        <div class="mobile:hidden laptop:flex flex-wrap justify-center items-center gap-[20px] mt-[10px]">
          <EvalButton variant="back" @click="backButtonClicked()" />
          <EvalButton @click="nextButtonClicked()" />
        </div>
      </template>

      <!-- below laptop: after the Details card (Figma mobile) -->
      <template #mobile-actions>
        <div class="absolute -bottom-[30px] left-0 right-0 laptop:relative laptop:bottom-0 flex flex-wrap justify-center items-center gap-[20px] laptop:mt-[30px] mobile:mb-[10px] laptop:mb-0">
          <EvalButton variant="back" @click="backButtonClicked()" />
          <EvalButton @click="nextButtonClicked()" />
        </div>
      </template>

      <!-- add items Modal -->
      <div id="addItemModal" ref="addItemModal" class="modal fade" data-bs-backdrop="static" data-bs-keyboard="false"
        tabindex="-1" aria-labelledby="exampleModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-md">
          <div class="modal-content">
            <div class="modal-body">
              <!-- <div class="closebutton d-flex justify-content-end">
                <button
                  type="button"
                  class="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  @click="hideModal"
                ></button>
              </div> -->
              <div class="modalCntnt pt-3">
                <h5 class="mb-0 blueText fw-bold text-center mb-3">
                  Misc. Items
                </h5>
                <div class="form-group mb-3">
                  <label class="mb-2">Title</label>
                  <input type="text" class="form-control" placeholder="Add">
                </div>
                <div class="form-group mb-3">
                  <label class="mb-2">Price</label>
                  <input type="text" class="form-control" placeholder="$12">
                </div>
                <div class="bottomButtons d-flex justify-content-center mt-5">
                  <button class="btn buttonPrimary d-flex align-items-center me-2" data-bs-dismiss="modal">
                    Cancel
                  </button>
                  <button class="btn buttonDark d-flex align-items-center">
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- selected part Modal -->
      <div id="selectPartModal" ref="selectPartModal" class="modal fade fxModal" data-bs-backdrop="static"
        data-bs-keyboard="false" tabindex="-1" aria-labelledby="staticBackdropLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" style="max-width: 460px">
          <div class="modal-content">
            <div class="modal-body p-0">
              <nest_select_part_modal v-if="bookingResponse.is_flat_price_model" :item="selectedItem"
                :booking-parts="componentList" :is-open="isOpen" :index="selectedIndex" @event="callback" />
              <nest_select_part_modal v-else :item="selectedItem" :booking-parts="bookingParts" :is-open="isOpen"
                :index="selectedIndex" @event="callback" />
            </div>
          </div>
        </div>
      </div>

      <addServiceModal ref="addingModal" @save="servicesAdded" />


    </EvalShell>
  </main>
</template>

<script>
import EvalShell from "@/components/nest_components/EvalShell.vue";
import EvalButton from "@/components/nest_components/EvalButton.vue";
import EvalComponentCard from "@/components/nest_components/EvalComponentCard.vue";
import EvalServiceRow from "@/components/nest_components/EvalServiceRow.vue";
import { mapActions, mapGetters } from "vuex";
import nest_selected_part from "../../components/nest_components/nest_selected_part.vue";
import nest_additional_table from "../../components/nest_components/nest_additional_table.vue";
import nest_select_part_modal from "../../components/nest_components/nest_select_part_modal.vue";
import { Modal } from "bootstrap";
import { useToast } from "vue-toastification";
import addServiceModal from "../../components/nest_components/addServiceModal.vue";
import nest_added_services from "../../components/nest_components/nest_added_services.vue";

const toast = useToast();
export default {
  components: {
    EvalShell,
    EvalButton,
    EvalComponentCard,
    EvalServiceRow,
    nest_selected_part,
    nest_additional_table,
    nest_select_part_modal,
    addServiceModal,
    nest_added_services
  },

  computed: {
    ...mapGetters([
      "preChecksDetail",
      "getDiagnosisOptions",
      "bookingServices",
      "bookingParts",
      "storeChecksResponse",
      "collectDataResponse",
      "allDiagnosisOptions",
      "bookingResponse",
      "componentList",
      "preChecksStore"
    ]),

    showElectricCard() {
      return this.selectedService && this.selectedPart;
    },
  },

  data() {
    return {
      modal: null,
      selectedService: "",
      selectedPart: "",
      options: [],
      additionalItems: [],
      // Services from the Add Service modal, sent in the same array as options
      addedServices: [],
      // components without a service after the last Next (Figma D5 banner)
      missingServiceCount: 0,
      initialChecks: [],
      preDiagnosis: null,
      jobId: this.$route.params.id,
      unit_id: this.$route.query.unit_id,
      selectedItem: null,
      selectedIndex: 0,
      isOpen: false,
      isApiCalled: false,
      selectedType: 'recommended'
    };
  },

  watch: {

    preChecksStore: function () {
      this.$router.push({
        path: `/summary-detail/${this.$route.params.id}`,
        query: {
          unit_id: this.unit_id,
          is_from_mobile: this.$route.query.is_from_mobile,
        },
      });
    },

    preChecksDetail: function () {

      if (this.preChecksDetail.length <= 0) {

        var params = {}
        params["is_diagnosis_fee_applicable"] = 0
        params["booking_id"] = this.jobId;
        this.storePreChecksDetails(params);

      }
      else {
        this.$router.push({
          path: `/final-evaluation/${this.$route.params.id}`,
          query: {
            unit_id: this.unit_id,
            is_from_mobile: this.$route.query.is_from_mobile,
          },
        });
      }

    },
    bookingServices: function () {
      this.getAllDiagnosisOption({
        booking_id: this.jobId,
      });
    },

    storeChecksResponse: function () {

      if (this.storeChecksResponse.diagnosis_type == "diagnosis_items") {
        this.storeChecks({
          booking_id: this.jobId,
          diagnosis_type: "added_services",
          diagnosis_data: JSON.stringify(this.addedServices),
        });
      } else if (this.storeChecksResponse.diagnosis_type == "added_services") {
        this.storeChecks({
          booking_id: this.jobId,
          diagnosis_type: "additional_items",
          diagnosis_data: JSON.stringify(this.additionalItems),
        });
      } else if (
        this.storeChecksResponse.diagnosis_type == "final_pre_checks"
      ) {
        this.getPreChecksDetails({
          booking_id: this.jobId,
        });
        // this.$router.push({
        //   path: `/final-evaluation/${this.$route.params.id}`,
        //   query: {
        //     unit_id: this.unit_id,
        //     is_from_mobile: this.$route.query.is_from_mobile,
        //   },
        // });
      } else {
        this.postCollectDataApi();
      }
    },

    allDiagnosisOptions: function () {
      this.handleDiagnosisResponse();
    },

    collectDataResponse: function () {
      this.storeChecks({
        booking_id: this.jobId,
        diagnosis_type: "final_pre_checks",
        diagnosis_data: JSON.stringify({}),
      });
    },
    bookingResponse: function () {
      this.handleBookingResponse();
    },
  },

  mounted() {
    this.getBookingDetails({
      booking_id: this.jobId,
    });
  },

  beforeUnmount() {
    const modalElement = document.getElementById("selectPartModal");
    modalElement.removeEventListener("show.bs.modal", this.addOverflowHidden);
    modalElement.removeEventListener(
      "hidden.bs.modal",
      this.removeOverflowHidden
    );
  },

  methods: {

    addClicked() {
      this.$refs.addingModal.show();
    },

    servicesAdded(services, editIndex) {
      if (editIndex !== null) {
        // Keep parts already selected on the card
        const parts = this.addedServices[editIndex].parts ?? [];
        this.addedServices.splice(editIndex, 1, { ...services[0], parts });
      } else {
        this.addedServices.push(...services);
      }
    },

    editService(index) {
      this.$refs.addingModal.show(this.addedServices[index], index);
    },

    removeService(index) {
      this.$dialog.show({
        title: "Remove Service",
        text: "Are you sure you want to remove this service?",
        onConfirm: () => {
          this.addedServices.splice(index, 1);
        },
      });
    },

    diagnosticOptionsWithServices() {
      return this.options.concat(this.addedServices);
    },

    removePart(data, item) {
      var i = item;
      i.parts = i.parts.filter(function (item) {
        return item !== data;
      });
    },

    addOverflowHidden() {
      document.documentElement.classList.add("overflow-hidden");
    },

    removeOverflowHidden() {
      document.documentElement.classList.remove("overflow-hidden");
    },

    ...mapActions([
      "getPreChecksDetails",
      "getDiagnosisOption",
      "getBookingServices",
      "getBookingParts",
      "getAllDiagnosisOption",
      "storeChecks",
      "postCollectData",
      "getBookingDetails",
      "getComponentList",
      "storePreChecksDetails"
    ]),
    handleBookingResponse() {
      if (this.bookingResponse !== null) {
        this.getBookingServices({ booking_id: this.jobId });

        // if (this.bookingResponse.is_flat_price_model) {
        //   this.getComponentList({});
        // } else {
        //   this.getBookingParts({
        //     category_id: "0",
        //     booking_id: this.jobId,
        //   });
        // }
      }
    },
    postCollectDataApi() {
      this.postCollectData({
        booking_id: this.jobId,
        additional_items: JSON.stringify(this.additionalItems),
        diagnostic_options: JSON.stringify(this.diagnosticOptionsWithServices()),
        initial_check: "[]", //JSON.stringify(this.initialChecks),
        booking_diagnostics: JSON.stringify(this.preDiagnosis),
        booking_notes: "[]",
        booking_pre_checks: "[]",

        equipment_checks: null,
        diagnosis_items: null,
      });
    },

    callback(data, opt) {
      this.isOpen = false;
      if (opt != "close") {
        this.selectedItem.parts = data.parts;
        if (this.selectedItem.parts.length > 0) {
          this.selectedItem["is_error"] = false;
          this.selectedItem["error"] = "";
        }
      }
      this.modal.hide();
    },

    handleDiagnosisResponse() {
      let self = this;
      this.allDiagnosisOptions.forEach(function (obj) {
        if (obj.diagnosis_type == "equipment_checks") {
          self.equipment_checks = obj;
        } else if (obj.diagnosis_type == "diagnosis_items") {
          self.diagnosis_items = obj;
        } else if (obj.diagnosis_type == "additional_items") {
          self.setAdditionalItems(obj);
        } else if (obj.diagnosis_type == "added_services") {
          self.setAddedServices(obj);
        } else if (obj.diagnosis_type == "initial_checks") {
          self.setInitialChecks(obj);
        } else if (obj.diagnosis_type == "pre-diagnosis") {
          self.setPreDiagnoisis(obj);
        }
      });

      this.setOptions(this.equipment_checks);
      this.setExistingOptions(this.diagnosis_items);
    },

    onChange(data) {
      data["booking_action_id"] = data.selected_service.id;
      data["is_error"] = false;
      data["error"] = "";
      if (this.missingServiceCount > 0) {
        this.missingServiceCount = this.options.filter(option => !option.booking_action_id).length;
      }
    },

    setOptions(data) {
      // no equipment diagnosis saved yet (summary opened directly): nothing to list
      if (!data?.diagnosis_data) return;
      let diagnosis_data = JSON.parse(data.diagnosis_data);
      let indoorExistingOptions = diagnosis_data.indoor;
      let outdoorExistingOptions = diagnosis_data.outdoor;
      this.options = indoorExistingOptions.concat(outdoorExistingOptions);

      this.options = this.options.filter(function (item) {
        return item.value != 1 && item.value != 0;
      });

      this.options.sort((a, b) => {
        if (a.value === 2 && b.value === 3) {
          return 1; // a comes before b
        } else if (a.value === 3 && b.value === 2) {
          return -1; // b comes before a
        } else {
          return 0; // no change in order
        }
      });

      this.isApiCalled = true;
    },

    setExistingOptions(data) {
      if (!data?.diagnosis_data) return;
      let diagnosis_data = JSON.parse(data.diagnosis_data);
      let ExistingOptions = diagnosis_data;

      this.options = this.options.map(function (item) {
        if (ExistingOptions.length > 0) {
          ExistingOptions.forEach(function (obj) {
            if (
              parseInt(obj.diagnostic_option_id) ===
              parseInt(item.diagnostic_option_id)
            ) {
              item = obj;
            }
          });
        }
        return item;
      });

      setTimeout(() => {
        const modalElement = document.getElementById("selectPartModal");
        modalElement.addEventListener("show.bs.modal", this.addOverflowHidden);
        modalElement.addEventListener(
          "hidden.bs.modal",
          this.removeOverflowHidden
        );
      }, 200);
    },

    setAdditionalItems(data) {
      let diagnosis_data = JSON.parse(data.diagnosis_data);
      this.additionalItems = diagnosis_data ?? [];
    },

    setAddedServices(data) {
      let diagnosis_data = JSON.parse(data.diagnosis_data);
      // diagnosis_data can be {} or null when nothing has been saved yet
      this.addedServices = Array.isArray(diagnosis_data) ? diagnosis_data : [];
    },

    setPreDiagnoisis(data) {
      let diagnosis_data = JSON.parse(data.diagnosis_data);
      this.preDiagnosis = diagnosis_data ?? null;
    },

    setInitialChecks(data) {
      let diagnosis_data = JSON.parse(data.diagnosis_data);
      this.initialChecks = diagnosis_data ?? [];
    },

    nextButtonClicked() {

      if (this.isValid()) {
        this.storeChecks({
          booking_id: this.jobId,
          diagnosis_type: "diagnosis_items",
          diagnosis_data: JSON.stringify(this.options),
        });
      }

    },

    backButtonClicked() {
      this.$router.go(-1);
    },

    getBookingActions(value) {
      if (value == 3) {
        return this.bookingServices.actions.required;
      } else if (value == 2) {
        return this.bookingServices.actions.recommended;
      }
    },

    dropDownDefaultValue(data) {
      if (data == undefined) {
        return data;
      } else {
        return null;
      }
    },
    // old value minor = 1, major = 2, maintenance = 5
    // new value minor = 2, major = 3, maintenance = 14
    isDisableParts(service) {
      if (this.bookingResponse.is_flat_price_model) {
        return false;
      } else {

        return false
        // if (
        //   service == undefined ||
        //   service == null ||
        //   (service != null && service.id == "14")
        // ) {
        //   return true;
        // } else {
        //   return false;
        // }
      }
    },

    getSelectedParts() {
      return (this.selectedItem != null && this.selectedItem.parts) ?? [];
    },

    closeModal() {
      this.isOpen = false;
      this.modal.hide();
    },

    openPartsModal(data, index) {
      this.selectedItem = data;
      this.selectedIndex = index;
      if (!this.selectedItem.parts) {
        this.selectedItem.parts = [];
      }
      this.isOpen = true;
      this.modal = new Modal(this.$refs.selectPartModal);
      this.modal.show();
    },

    isValid() {
      let res = true;
      let msg = "";
      let index = -1;
      let scrollId = null;
      if (this.options) {
        for (var indx in this.options) {
          if (!this.options[indx].booking_action_id) {
            //fllat model handling
            if (this.bookingResponse.is_flat_price_model) {
              msg = "";
              res = true;
              this.options[indx].is_error = false;
              this.options[indx].error = "";
            } else {
              msg = "";
              res = false;
              this.options[indx].is_error = true;
              this.options[indx].error = "Service selection is required";

              if (index == -1) {
                index = indx;
              }
            }


            // old value minor = 1, major = 2, maintenance = 5
            // new value minor = 2, major = 3, maintenance = 14
            // break outerLoop;
          } else if (
            this.options[indx].booking_action_id == 3 &&
            (!this.options[indx].parts || this.options[indx].parts.length < 1)
          ) {
            this.options[indx].is_error = true;
            this.options[indx].error = "Parts selection is required";
            msg = "";
            res = false;
            if (index == -1) {
              index = indx;
            }
            // break outerLoop;
          }
        }
      }

      if (index != -1) {
        scrollId = "" + index;
      }
      this.missingServiceCount = this.bookingResponse.is_flat_price_model
        ? 0
        : this.options.filter(option => !option.booking_action_id).length;

      // Added services always have a service, only parts are checked
      this.addedServices.forEach((service, i) => {
        if (
          service.booking_action_id == 3 &&
          (!service.parts || service.parts.length < 1)
        ) {
          service.is_error = true;
          service.error = "Parts selection is required";
          res = false;
          if (scrollId == null) {
            scrollId = "service-" + i;
          }
        }
      });

      if (res) {
        this.additionalItems.forEach((element) => {
          if (element.title.length <= 0) {
            msg = "Title field is required.";
            res = false;
          } else if (element.price.length <= 0) {
            msg = "Price field is required.";
            res = false;
          }
        });
      }

      // parts are edited inline now (Figma D3), so the old edit-modal price checks run here
      if (res && !this.bookingResponse.is_flat_price_model) {
        const parts = [...this.options, ...this.addedServices].flatMap(option => option.parts || []);
        for (const part of parts) {
          if (!part.is_warranty && (part.cost_with_markup === '' || part.cost_with_markup == null)) {
            msg = "Price field is required.";
            res = false;
            break;
          }
          if (part.is_max_markup_applicable && Number(part.cost_with_markup) > Number(part.max_allowed_price)) {
            msg = "Please fix price errors before saving";
            res = false;
            break;
          }
        }
      }

      if (!res && msg.length > 0) {
        toast.error(msg);
      }

      if (!res && scrollId != null) {
        window.scrollTo({
          top: document.getElementById(scrollId).offsetTop,
          left: 0,
          behavior: "smooth",
        });
      }

      return res;
    },
  },
};
</script>

<style>
/* .summaryPg .nestNotesModal button {
  min-width: auto;
  padding: 0 !important;
} */
.editPart img {
  width: 35px;
}

.flatPriceSec {
  box-shadow: 0px 0px 5px 0 rgb(0 0 0 / 17%);
  padding: 1rem;
  margin-bottom: 1rem;
  position: relative;
}

.borderLeft {
  position: absolute;
  top: 15px;
  left: 0;
  width: 6px;
  height: 30px;
  border-radius: 0 5px 5px 0;
}

.borderLeftRed {
  background: #f6635c;
}

.borderLeftYellow {
  background: #FFB000;
}

.electricDuckHead .buttonSecondary {
  border-radius: 50px !important;
  font-size: 13px !important;
  padding: 0.2rem 0.8rem !important;
}

.selectedPart h6 {
  background: rgba(9, 46, 135, 0.1);
  padding: 0.2rem 0.5rem;
  font-size: 14px;
  color: #092e87;
  border-radius: 3px;
}

.selectedPart ul li {
  position: relative;
  padding-left: 1rem;
  margin-bottom: 0.5rem;
}

.selectedPart ul li:before {
  position: absolute;
  content: "";
  top: 10px;
  left: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #092e87;
}

.selectedPart ul li:last-child {
  margin-bottom: 0;
}

.selectedPart ul li p {
  font-size: 14px;
}

.selectedPart li img {
  width: 25px;
  height: 25px;
}
</style>
